import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/joy";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import type { SkillInvocationAttribution } from "../../../common/types";
import {
  getAttributionState,
  getAttributionSummary,
  type AttributionRenderState,
} from "./skillInvocationAttributionState";

interface SkillInvocationAttributionCardProps {
  attributions: SkillInvocationAttribution[];
  enabled: boolean;
}

const formatArgs = (args: Record<string, unknown> | null) => {
  if (!args || Object.keys(args).length === 0) return "No input args";
  return JSON.stringify(args, null, 2);
};

const STATUS_META: Record<
  AttributionRenderState,
  {
    color: "success" | "warning" | "danger";
    label: string;
    icon: React.ReactElement;
    detailLabel: string;
  }
> = {
  success: {
    color: "success",
    label: "success",
    icon: <CheckCircleIcon />,
    detailLabel: "Output summary",
  },
  blocked: {
    color: "warning",
    label: "blocked",
    icon: <BlockIcon />,
    detailLabel: "Block reason",
  },
  error: {
    color: "danger",
    label: "error",
    icon: <ErrorOutlineIcon />,
    detailLabel: "Error",
  },
};

export const SkillInvocationAttributionCard: React.FC<
  SkillInvocationAttributionCardProps
> = ({ attributions, enabled }) => {
  const [expanded, setExpanded] = React.useState(false);
  const panelId = React.useId();

  if (!enabled || attributions.length === 0) {
    return null;
  }

  const label =
    attributions.length === 1
      ? `via skill ${attributions[0].skill_name}`
      : `via ${attributions.length} skills`;

  return (
    <Stack
      spacing={0.75}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        alignItems: "stretch",
      }}
      data-testid="skill-attribution-card"
    >
      <Button
        size="sm"
        variant="soft"
        color="neutral"
        onClick={() => setExpanded((prev) => !prev)}
        sx={{ borderRadius: 6, px: 1.25, minHeight: 30, alignSelf: "flex-start" }}
        data-testid="skill-attribution-toggle"
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
      >
        {label}
      </Button>
      {expanded && (
        <Card
          variant="soft"
          id={panelId}
          data-testid="skill-attribution-panel"
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            p: 1.5,
            borderRadius: 6,
            border: "1px solid",
            borderColor: "divider",
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={1.25}>
            {attributions.map((attribution, index) => (
              <React.Fragment key={attribution.id}>
                {index > 0 && <Divider />}
                {(() => {
                  const state = getAttributionState(attribution);
                  const status = STATUS_META[state];
                  return (
                    <Stack spacing={0.75}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography level="title-sm">
                          {attribution.skill_name}
                        </Typography>
                        <Chip
                          size="sm"
                          color={status.color}
                          variant="soft"
                          startDecorator={status.icon}
                        >
                          {status.label}
                        </Chip>
                      </Box>
                      <Typography level="body-xs" color="neutral">
                        Duration:{" "}
                        {attribution.duration_ms !== null
                          ? `${attribution.duration_ms} ms`
                          : "n/a"}
                      </Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography level="body-xs" color="neutral">
                          Input args
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            mt: 0.5,
                            p: 1,
                            borderRadius: 4,
                            bgcolor: "background.level1",
                            fontSize: "12px",
                            overflowX: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {formatArgs(attribution.input_args)}
                        </Box>
                      </Box>
                      <Box>
                        <Typography level="body-xs" color="neutral">
                          {status.detailLabel}
                        </Typography>
                        <Alert
                          color={status.color}
                          variant="soft"
                          sx={{ mt: 0.75, alignItems: "flex-start" }}
                        >
                          {getAttributionSummary(attribution)}
                        </Alert>
                      </Box>
                    </Stack>
                  );
                })()}
              </React.Fragment>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};

export default SkillInvocationAttributionCard;
